import * as React from "react";

import { cn } from "@/shared/lib/utils";

import { Progress, ProgressIndicator, ProgressTrack } from "../primitives/progress";

export type ColocaProgressTone = "primary" | "success" | "warning" | "danger" | "neutral";

export interface ColocaProgressSegment {
  /** Stable key — the outcome the segment stands for (`paid`, `failed`, …). */
  key: string;
  /** Share of the bar, on the same scale as `max`. Segments are stacked left to right. */
  value: number;
  tone: ColocaProgressTone;
  /** Accessible name of the segment, read into the bar's `aria-valuetext`. */
  label?: string;
}

export interface ColocaProgressProps extends Omit<React.ComponentPropsWithoutRef<"div">, "children"> {
  /** Completed amount on the `max` scale. `null` renders the indeterminate bar for a task of unknown length. */
  value: number | null;
  /** Scale of `value` and of every segment. @default 100 */
  max?: number;
  /** Caption above the bar. Also names the bar when no `aria-label` is given and it is a plain string. */
  label?: React.ReactNode;
  /** Prints the rounded percentage at the end of the caption row. */
  showValue?: boolean;
  size?: "sm" | "default" | "lg";
  /** Colour of the single indicator. Ignored when `segments` is set. */
  tone?: ColocaProgressTone;
  /**
   * Stacked outcomes instead of one indicator — paid, failed and in-flight rows of a batch. Their sum should
   * not exceed `max`; `value` still drives the accessible position, so pass it the sum of what counts as
   * done.
   */
  segments?: ColocaProgressSegment[];
  "aria-label"?: string;
}

const TRACK_SIZE: Record<NonNullable<ColocaProgressProps["size"]>, string> = {
  sm: "h-1.5",
  default: "h-2",
  lg: "h-3",
};

const TONE_FILL: Record<ColocaProgressTone, string> = {
  primary: "bg-primary",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-destructive",
  neutral: "bg-muted-foreground/40",
};

const clampPercent = (value: number, max: number) => Math.min(100, Math.max(0, (value / max) * 100));

/**
 * Progress bar for work that moves while the person watches — a batch validating, rows being paid, a file
 * uploading. `value={null}` says "still working, unknown how long"; `segments` tells the story of a finished
 * or moving batch in one strip (what was paid, what failed, what is still in flight) instead of three
 * numbers.
 *
 * @summary determinate, indeterminate or segmented progress bar with accessible value
 */
export const ColocaProgress = React.forwardRef<HTMLDivElement, ColocaProgressProps>(function ColocaProgress(
  {
    value,
    max = 100,
    label,
    showValue = false,
    size = "default",
    tone = "primary",
    segments,
    "aria-label": ariaLabel,
    className,
    ...rest
  },
  ref,
) {
  const percent = value === null ? null : clampPercent(value, max);
  const hasSegments = segments !== undefined && segments.length > 0;
  const valueText = hasSegments
    ? segments
        .filter((segment) => segment.value > 0)
        .map((segment) => `${segment.label ?? segment.key}: ${segment.value}`)
        .join(", ")
    : undefined;

  return (
    <Progress
      ref={ref}
      value={value}
      max={max}
      aria-label={ariaLabel ?? (typeof label === "string" ? label : undefined)}
      aria-valuetext={valueText}
      className={className}
      {...rest}
    >
      {(label != null || (showValue && percent !== null)) && (
        <div className="flex items-baseline justify-between gap-3 text-xs">
          {label != null ? <span className="text-muted-foreground">{label}</span> : <span />}
          {showValue && percent !== null && (
            <span className="font-mono text-foreground tabular-nums">{Math.round(percent)}%</span>
          )}
        </div>
      )}
      <ProgressTrack className={TRACK_SIZE[size]}>
        {hasSegments ? (
          segments.map((segment) => (
            <span
              key={segment.key}
              data-segment={segment.key}
              aria-hidden="true"
              className={cn(
                "h-full transition-[width] duration-300 ease-out motion-reduce:transition-none",
                TONE_FILL[segment.tone],
              )}
              style={{ width: `${clampPercent(segment.value, max)}%` }}
            />
          ))
        ) : (
          <ProgressIndicator className={TONE_FILL[tone]} />
        )}
      </ProgressTrack>
    </Progress>
  );
});
